import { ERole } from '@dlap-ui/core';
import type { PortalConfig } from '@dlap-ui/shared-forms/shell/portal-shell/portal-shell';
import { LAW_FIRM_DASHBOARD_PAGES } from './dashboard-pages.config';

export const LAW_FIRM_PORTAL_CONFIG: PortalConfig = {
  appTitle: 'Econveyancer',
  logoSrc: '/law-firm-logo.png',
  searchPlaceholder: 'Search applications...',
  defaultRedirect: '/overview/dashboard',
  rolePermissions: [
    {
      role: ERole.CONVEYANCER,
      permissions: ['applications', 'drafts', 'archived', 'tickets', 'wallet', 'fees', 'individuals-biometrics'],
    },
    {
      role: ERole.NOTARY,
      permissions: ['applications', 'drafts', 'archived', 'tickets', 'wallet', 'fees', 'individuals-biometrics'],
    },
    {
      role: ERole.LAWYER,
      permissions: ['applications', 'drafts', 'archived', 'tickets', 'wallet', 'fees', 'individuals-biometrics'],
    },
    {
      role: ERole.REGULAR_USER,
      permissions: ['tickets', 'wallet', 'fees'],
    },
    {
      role: ERole.LAW_FIRM_ADMIN,
      permissions: ['applications', 'drafts', 'archived', 'tickets', 'wallet', 'fees', 'users', 'memberships/members'],
    },
  ],
  dashboardPages: LAW_FIRM_DASHBOARD_PAGES,
  /**
   * The services a law firm can apply for, in the order the old project's
   * modal listed them. The shared catalog also carries services belonging to
   * other portals (e.g. Note of Hand for banks, Caveat Upliftment for the
   * High Court), which is why this portal names its own rather than showing
   * the whole catalog.
   *
   * Every entry here has its own step-by-step page under /overview/apply/*.
   * Caveat Registration, Lease Cancellation and Rectification are absent
   * because they had none: they were served only by the generic
   * metadata-driven workflow, which this portal no longer has. Add them back
   * alongside their own pages.
   */
  newApplicationServiceKeys: [
    'notarial-deed-section',
    'trust-registration',
    'deed-transfer',
    'deed-subdivision',
    'deed-consolidation',
    'sectional-title',
    'land-tenure-transfer',
    'land-tenure-subdivision',
    'deed-re-issuance',
    'estate',
    'bond-registration',
    'bond-cancellation',
  ],
  resolveApplicationNavigation: (service) => {
    if (service.value === 'deed-re-issuance') {
      return {
        commands: ['/overview/apply/reissuance'],
      };
    }

    // Deed of Transfer has its own step-by-step page rather than the
    // metadata-driven workflow, same as reissuance.
    if (service.value === 'deed-transfer') {
      return {
        commands: ['/overview/apply/transfer'],
      };
    }

    // Only this portal has dedicated bond pages — the bank portal still
    // reaches both bond services through the generic workflow, so their
    // shared workflow definitions stay in place.
    if (service.value === 'bond-registration') {
      return {
        commands: ['/overview/apply/bond-registration'],
      };
    }

    if (service.value === 'bond-cancellation') {
      return {
        commands: ['/overview/apply/bond-cancellation'],
      };
    }

    if (service.value === 'sectional-title') {
      return {
        commands: ['/overview/apply/sectional-title'],
      };
    }

    if (service.value === 'trust-registration') {
      return {
        commands: ['/overview/apply/trust-registration'],
      };
    }

    if (service.value === 'notarial-deed-section') {
      return {
        commands: ['/overview/apply/notarial-deed-section'],
      };
    }

    if (service.value === 'deed-consolidation') {
      return {
        commands: ['/overview/apply/consolidation'],
      };
    }

    if (service.value === 'deed-subdivision') {
      return {
        commands: ['/overview/apply/subdivision'],
      };
    }

    if (service.value === 'land-tenure-transfer') {
      return {
        commands: ['/overview/apply/land-tenure-transfer'],
      };
    }

    if (service.value === 'land-tenure-subdivision') {
      return {
        commands: ['/overview/apply/land-tenure-subdivision'],
      };
    }

    if (service.value === 'estate') {
      return {
        commands: ['/overview/apply/estate'],
      };
    }

    // Unreachable for anything this portal lists — every one of those has a
    // branch above. Kept honest rather than pointing at the removed generic
    // workflow route.
    return {
      commands: ['/overview/applications'],
      successMessage: `${service.name} is not available yet.`,
    };
  },
};
